import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useLang } from "../lib/i18n";
import { PageHeader } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Loader2, ShieldAlert, BookOpen, Check, X, Pencil } from "lucide-react";

const SEV = { high: "bg-red-100 text-red-700", medium: "bg-amber-100 text-amber-800", low: "bg-slate-100 text-slate-600" };
const DEC = { pending: "À traiter", accepted: "Acceptée", corrected: "Corrigée", rejected: "Rejetée" };

function Row({ it, onDecide }) {
  const { t } = useLang();
  const [val, setVal] = useState(it.approved_value ?? "");
  const [note, setNote] = useState("");
  const k = `${it.entry_id}-${it.field}-${it.code}`;
  return (
    <tr className="border-b border-slate-100 align-top text-sm" data-testid={`dq-row-${k}`}>
      <td className="py-2 pr-3"><span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${SEV[it.severity]}`}>{it.code}</span><div className="text-[11px] text-slate-400 mt-1">{it.date} · {it.health_zone} · {it.author_name}</div></td>
      <td className="py-2 pr-3 font-mono text-xs text-slate-700">{it.field}<div className="text-slate-900 font-sans text-sm font-semibold">{it.original ?? "—"}</div></td>
      <td className="py-2 pr-3 text-slate-800">{it.message}</td>
      <td className="py-2 pr-3 text-xs text-slate-600">{it.suggestion}</td>
      <td className="py-2 pr-3">
        {it.decision === "pending" ? (
          <div className="flex flex-col gap-1.5 min-w-[220px]">
            <div className="flex gap-1.5">
              <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder={t("Valeur corrigée")} className="h-8 text-xs" data-testid={`dq-value-${k}`} />
              <Button size="sm" className="h-8 rounded-md bg-[#0F172A]" disabled={val === ""} onClick={() => onDecide(it, "corrected", val, note)} data-testid={`dq-correct-${k}`}><Pencil className="h-3.5 w-3.5" /></Button>
            </div>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("Note de vérification")} className="h-8 text-xs" data-testid={`dq-note-${k}`} />
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" className="h-8 rounded-md text-emerald-700" onClick={() => onDecide(it, "accepted", null, note)} data-testid={`dq-accept-${k}`}><Check className="h-3.5 w-3.5 mr-1" />{t("Valeur confirmée")}</Button>
              <Button size="sm" variant="outline" className="h-8 rounded-md text-slate-500" onClick={() => onDecide(it, "rejected", null, note)} data-testid={`dq-reject-${k}`}><X className="h-3.5 w-3.5 mr-1" />{t("Faux positif")}</Button>
            </div>
          </div>
        ) : (
          <div className="text-xs" data-testid={`dq-decision-${k}`}>
            <span className="font-semibold text-slate-800">{t(DEC[it.decision])}</span>
            {it.decision === "corrected" && <span> → <b>{it.approved_value}</b></span>}
            <div className="text-slate-400">{it.reviewer}{it.note ? ` · ${it.note}` : ""}</div>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function DataQualityReview() {
  const { t } = useLang();
  const [tab, setTab] = useState("review");
  const [status, setStatus] = useState("pending");
  const [data, setData] = useState(null);
  const [dict, setDict] = useState([]);

  const load = useCallback(async () => {
    try {
      const [r, d] = await Promise.all([api.get("/dq/review", { params: { days: 90, ...(status ? { status } : {}) } }), api.get("/indicators/dictionary")]);
      setData(r.data); setDict(d.data);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const decide = async (it, decision, approved_value, note) => {
    try {
      await api.put(`/dq/review/${it.entry_id}/${it.field}/${it.code}`, { decision, approved_value, note });
      toast.success(t("Décision enregistrée."));
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="dq-page">
      <PageHeader eyebrow={t("Supervision")} title={t("Qualité des données")}
        subtitle={t("Valeur d'origine → problème détecté → vérification suggérée → décision du superviseur → valeur approuvée. Les valeurs d'origine ne sont jamais écrasées.")} testid="dq-header" />
      <div className="flex gap-2 mb-4">
        <button type="button" onClick={() => setTab("review")} className={`h-9 px-3 rounded-md text-sm font-medium inline-flex items-center gap-1.5 ${tab === "review" ? "bg-[#0F172A] text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid="dq-tab-review"><ShieldAlert className="h-4 w-4" />{t("Réconciliation")} {data && <span className="ml-1 text-xs opacity-80">({data.counts.pending})</span>}</button>
        <button type="button" onClick={() => setTab("dict")} className={`h-9 px-3 rounded-md text-sm font-medium inline-flex items-center gap-1.5 ${tab === "dict" ? "bg-[#0F172A] text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid="dq-tab-dict"><BookOpen className="h-4 w-4" />{t("Dictionnaire des indicateurs")} ({dict.length})</button>
      </div>

      {tab === "review" && (
        <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-4">
          <div className="flex flex-wrap gap-2 mb-3">
            {Object.entries(DEC).map(([k, v]) => (
              <button key={k} type="button" onClick={() => setStatus(k)} className={`text-xs rounded-md px-2.5 py-1 border ${status === k ? "border-[#F05A28] bg-[#FFF8F5] text-[#F05A28]" : "border-slate-200 text-slate-600"}`} data-testid={`dq-filter-${k}`}>{t(v)}</button>
            ))}
            <button type="button" onClick={() => setStatus("")} className={`text-xs rounded-md px-2.5 py-1 border ${status === "" ? "border-[#F05A28] text-[#F05A28]" : "border-slate-200 text-slate-600"}`} data-testid="dq-filter-all">{t("Toutes")}</button>
          </div>
          {!data ? <Loader2 className="h-5 w-5 animate-spin text-[#F05A28]" /> : data.items.length === 0 ? (
            <p className="text-sm text-slate-500 italic py-8 text-center" data-testid="dq-empty">{t("Aucune anomalie pour ce filtre.")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="dq-table">
                <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b">
                  <th className="py-2 pr-3">{t("Règle")}</th><th className="py-2 pr-3">{t("Valeur d'origine")}</th><th className="py-2 pr-3">{t("Problème détecté")}</th><th className="py-2 pr-3">{t("Vérification suggérée")}</th><th className="py-2">{t("Décision")}</th>
                </tr></thead>
                <tbody>{data.items.map((it) => <Row key={`${it.entry_id}-${it.field}-${it.code}`} it={it} onDecide={decide} />)}</tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "dict" && (
        <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-4 overflow-x-auto" data-testid="dq-dictionary">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b">
              <th className="py-2 pr-3">{t("Indicateur")}</th><th className="py-2 pr-3">{t("Numérateur")}</th><th className="py-2 pr-3">{t("Dénominateur")}</th><th className="py-2 pr-3">{t("Agrégation")}</th><th className="py-2 pr-3">{t("Périodes")}</th><th className="py-2 pr-3">{t("Cible")}</th><th className="py-2">{t("Calculé")}</th>
            </tr></thead>
            <tbody>
              {dict.map((d) => (
                <tr key={d.code} className="border-b border-slate-100" data-testid={`dict-${d.code}`}>
                  <td className="py-2 pr-3"><b className="text-slate-800">{d.label}</b><div className="font-mono text-[11px] text-slate-400">{d.code}</div></td>
                  <td className="py-2 pr-3 font-mono text-xs">{d.numerator}</td><td className="py-2 pr-3 font-mono text-xs">{d.denominator}</td>
                  <td className="py-2 pr-3 text-xs">{d.aggregation}</td><td className="py-2 pr-3 text-xs">{d.periods}</td><td className="py-2 pr-3 text-xs">{d.target}</td>
                  <td className="py-2 text-xs">{d.computed ? <span className="text-emerald-700 font-semibold">{t("Oui")}</span> : <span className="text-slate-400">{t("P3")}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
